import { nanoid } from 'nanoid'
import { SplineUtils } from '@/plugins/cad/module/utils'
import { getLoggerManager } from '@/shared/logging'
import { EntityType } from '../../model/common/constant'
import type { IPoint } from '../../model/common/interface'
import type {
  ArcEntity,
  CircleEntity,
  EllipseEntity,
  IEntity,
  IPolylineVertice,
  PolylineEntity,
  SplineEntity,
} from '../../model/entity'
import type { OffsetParams, OffsetSide } from '../EntityEditHandler'

const log = getLoggerManager().getLogger('cad')

/**
 * 偏移实体工具集合：按距离与方向对支持的实体生成平行副本。
 */
export class OffsetUtils {
  /** 可偏移的实体类型集合 */
  static readonly EnabledEntityTypes = new Set<EntityType>([
    EntityType.Polyline,
    EntityType.Arc,
    EntityType.Circle,
    EntityType.Spline,
    EntityType.Ellipse,
  ])

  /**
   * 偏移单个实体。
   *
   * @param entity - 源实体
   * @param params - 偏移距离与方向
   * @returns 偏移后的新实体；不支持或偏移无效时返回 null
   */
  static offsetEntity(entity: IEntity, params: OffsetParams): IEntity | null {
    const { distance, side } = params
    const d = distance * side

    switch (entity.type) {
      case EntityType.Circle: {
        const circle = entity as CircleEntity
        const newRadius = circle.radius + d
        if (newRadius <= 0) {
          return null
        }
        const cloned = circle.clone() as CircleEntity
        cloned.id = nanoid()
        cloned.radius = newRadius
        return cloned
      }
      case EntityType.Arc: {
        const arc = entity as ArcEntity
        const newRadius = arc.radius + d
        if (newRadius <= 0) {
          return null
        }
        const cloned = arc.clone() as ArcEntity
        cloned.id = nanoid()
        cloned.radius = newRadius
        return cloned
      }
      case EntityType.Polyline: {
        return OffsetUtils.offsetPolyline(entity as PolylineEntity, d)
      }
      case EntityType.Spline: {
        return OffsetUtils.offsetSpline(entity as SplineEntity, d)
      }
      case EntityType.Ellipse: {
        return OffsetUtils.offsetEllipse(entity as EllipseEntity, d)
      }
      default:
        log.warn(`Offset operation not implemented for entity type: ${entity.type}`)
        return null
    }
  }

  /**
   * 偏移多段线（支持直线段和凸度弧线段）
   *
   * 算法：
   *  1. 对每一段分别计算偏移表示：
   *     - 直线段 → 平行偏移线
   *     - 圆弧段（bulge ≠ 0） → 同心偏移圆弧（半径 ± d，圆心不变）
   *  2. 对每个顶点，计算相邻两个偏移段的交点，得到新顶点位置
   *     - 直线×直线 → 直线-直线交点
   *     - 直线×弧线 → 直线-圆交点（选最近候选）
   *     - 弧线×弧线 → 圆-圆交点（选最近候选）
   *  3. 根据新顶点位置与弧线圆心重新计算各段的 bulge 值
   *
   * 偏移方向约定（d > 0 为正偏移）：
   *  - 直线段：d > 0 → 沿行进方向左侧（逆时针法线）
   *  - CCW 弧（bulge > 0）：d > 0 → 向圆心方向（缩小半径）
   *  - CW 弧（bulge < 0）：d > 0 → 背离圆心方向（扩大半径）
   *  统一公式：new_radius = radius - sign(bulge) * d
   */
  private static offsetPolyline(polyline: PolylineEntity, d: number): PolylineEntity | null {
    const verts = polyline.vertices
    if (verts.length < 2) {
      return null
    }

    type PT = { x: number; y: number }

    // ── 工具函数 ──────────────────────────────────────────────────────────

    /** 由两端点和凸度值计算圆弧的圆心与半径 */
    const arcFromBulge = (p1: PT, p2: PT, b: number) => {
      const dx = p2.x - p1.x,
        dy = p2.y - p1.y
      const L = Math.hypot(dx, dy)
      if (L < 1e-10) {
        return null
      }
      // 弦中点
      const mx = (p1.x + p2.x) / 2,
        my = (p1.y + p2.y) / 2
      // 单位法向量（弦方向逆时针旋转 90°）
      const nx = -dy / L,
        ny = dx / L
      // 圆心到弦中点距离: d_c = (L/2) * (1 - b²) / (2b)
      const d_c = ((L / 2) * (1 - b * b)) / (2 * b)
      const cx = mx + nx * d_c
      const cy = my + ny * d_c
      const radius = Math.hypot(L / 2, d_c)
      return { cx, cy, radius }
    }

    /** 两直线（无限延伸）的交点，平行时取端点中点 */
    const lineLineIntersect = (p1: PT, p2: PT, p3: PT, p4: PT): PT => {
      const dx1 = p2.x - p1.x,
        dy1 = p2.y - p1.y
      const dx2 = p4.x - p3.x,
        dy2 = p4.y - p3.y
      const denom = dx1 * dy2 - dy1 * dx2
      if (Math.abs(denom) < 1e-10) {
        return { x: (p2.x + p3.x) / 2, y: (p2.y + p3.y) / 2 }
      }
      const t = ((p3.x - p1.x) * dy2 - (p3.y - p1.y) * dx2) / denom
      return { x: p1.x + t * dx1, y: p1.y + t * dy1 }
    }

    /**
     * 直线（过 lp1/lp2 无限延伸）与圆（cx,cy,r）的交点
     * 选取距离 refPt 最近的候选点
     */
    const lineCircleIntersect = (
      lp1: PT,
      lp2: PT,
      cx: number,
      cy: number,
      r: number,
      refPt: PT
    ): PT => {
      const dx = lp2.x - lp1.x,
        dy = lp2.y - lp1.y
      const len = Math.hypot(dx, dy)
      if (len < 1e-10) {
        return { x: cx + r, y: cy }
      }
      const ux = dx / len,
        uy = dy / len
      // 圆心在直线上的投影
      const t = (cx - lp1.x) * ux + (cy - lp1.y) * uy
      const px = lp1.x + t * ux,
        py = lp1.y + t * uy
      const distSq = (cx - px) ** 2 + (cy - py) ** 2
      const rSq = r * r
      if (distSq >= rSq - 1e-10) {
        // 相切或不相交，返回切点（在圆上的最近点）
        return { x: px, y: py }
      }
      const dt = Math.sqrt(rSq - distSq)
      const pt1: PT = { x: px + dt * ux, y: py + dt * uy }
      const pt2: PT = { x: px - dt * ux, y: py - dt * uy }
      const d1 = (pt1.x - refPt.x) ** 2 + (pt1.y - refPt.y) ** 2
      const d2 = (pt2.x - refPt.x) ** 2 + (pt2.y - refPt.y) ** 2
      return d1 <= d2 ? pt1 : pt2
    }

    /**
     * 两圆的交点，选取距离 refPt 最近的候选点
     */
    const circleCircleIntersect = (
      cx1: number,
      cy1: number,
      r1: number,
      cx2: number,
      cy2: number,
      r2: number,
      refPt: PT
    ): PT => {
      const dx = cx2 - cx1,
        dy = cy2 - cy1
      const dist = Math.hypot(dx, dy)
      if (dist < 1e-10) {
        // 同心圆，返回 refPt 方向上圆1上的点
        const a = Math.atan2(refPt.y - cy1, refPt.x - cx1)
        return { x: cx1 + r1 * Math.cos(a), y: cy1 + r1 * Math.sin(a) }
      }
      const a = (r1 * r1 - r2 * r2 + dist * dist) / (2 * dist)
      const h2 = r1 * r1 - a * a
      if (h2 < 0) {
        // 不相交，退回到圆1上朝 C2 方向的点
        return { x: cx1 + (r1 * dx) / dist, y: cy1 + (r1 * dy) / dist }
      }
      const h = Math.sqrt(h2)
      const nx = dx / dist,
        ny = dy / dist
      const mx = cx1 + a * nx,
        my = cy1 + a * ny
      const pt1: PT = { x: mx + h * ny, y: my - h * nx }
      const pt2: PT = { x: mx - h * ny, y: my + h * nx }
      const d1 = (pt1.x - refPt.x) ** 2 + (pt1.y - refPt.y) ** 2
      const d2 = (pt2.x - refPt.x) ** 2 + (pt2.y - refPt.y) ** 2
      return d1 <= d2 ? pt1 : pt2
    }

    // ── 闭合多段线：根据顶点绕向归一化偏移量 ──────────────────────────
    // 段偏移公式沿行进方向左转 90° 作为法线，即 normal = (-dy, dx) * d。
    // 对于 CCW 多边形，左侧 = 内部 → d>0 向内。
    // 但 offsetEntity 约定 d>0 = 向外（side=1=外），因此对 CCW 翻转 d。
    // 对于 CW 多边形，左侧 = 外部 → d>0 已是向外，无需翻转。
    if (polyline.isClosed) {
      let area2 = 0
      for (let i = 0, j = verts.length - 1; i < verts.length; j = i++) {
        area2 += verts[j].x * verts[i].y - verts[i].x * verts[j].y
      }
      if (area2 > 0) {
        d = -d
      } // CCW 绕向：翻转 d，使 d>0 代表向外
    }

    // ── 计算每段的偏移表示 ───────────────────────────────────────────────

    type StraightOff = { type: 'straight'; p1: PT; p2: PT }
    type ArcOff = { type: 'arc'; cx: number; cy: number; newRadius: number; origBulge: number }
    type SegOff = StraightOff | ArcOff

    const segCount = polyline.isClosed ? verts.length : verts.length - 1
    const segOffs: (SegOff | null)[] = []

    for (let i = 0; i < segCount; i++) {
      const v1 = verts[i]
      const v2 = verts[(i + 1) % verts.length]
      const bulge = v1.bulge || 0

      if (Math.abs(bulge) < 1e-10) {
        // 直线段：沿法线（行进方向左转 90°）平移 d
        const sdx = v2.x - v1.x,
          sdy = v2.y - v1.y
        const sLen = Math.hypot(sdx, sdy)
        if (sLen < 1e-10) {
          segOffs.push(null)
          continue
        }
        const nx = (-sdy / sLen) * d,
          ny = (sdx / sLen) * d
        segOffs.push({
          type: 'straight',
          p1: { x: v1.x + nx, y: v1.y + ny },
          p2: { x: v2.x + nx, y: v2.y + ny },
        })
      } else {
        // 圆弧段：同心偏移，半径变化 new_R = R - sign(b)*d
        const arc = arcFromBulge(v1, v2, bulge)
        if (!arc) {
          segOffs.push(null)
          continue
        }
        const newRadius = arc.radius - Math.sign(bulge) * d
        if (newRadius < 1e-10) {
          return null
        } // 圆弧向内消失
        segOffs.push({ type: 'arc', cx: arc.cx, cy: arc.cy, newRadius, origBulge: bulge })
      }
    }

    if (segOffs.some((s) => s === null)) {
      return null
    }

    // ── 计算相邻偏移段的交点 ─────────────────────────────────────────────

    /** 求 prevSeg 末端与 currSeg 首端的交点，refPt 用于多解时选近侧 */
    const intersectSegs = (prevSeg: SegOff, currSeg: SegOff, refPt: PT): PT => {
      if (prevSeg.type === 'straight' && currSeg.type === 'straight') {
        return lineLineIntersect(prevSeg.p1, prevSeg.p2, currSeg.p1, currSeg.p2)
      }
      if (prevSeg.type === 'straight' && currSeg.type === 'arc') {
        return lineCircleIntersect(
          prevSeg.p1,
          prevSeg.p2,
          currSeg.cx,
          currSeg.cy,
          currSeg.newRadius,
          refPt
        )
      }
      if (prevSeg.type === 'arc' && currSeg.type === 'straight') {
        return lineCircleIntersect(
          currSeg.p1,
          currSeg.p2,
          prevSeg.cx,
          prevSeg.cy,
          prevSeg.newRadius,
          refPt
        )
      }
      // arc × arc
      return circleCircleIntersect(
        (prevSeg as ArcOff).cx,
        (prevSeg as ArcOff).cy,
        (prevSeg as ArcOff).newRadius,
        (currSeg as ArcOff).cx,
        (currSeg as ArcOff).cy,
        (currSeg as ArcOff).newRadius,
        refPt
      )
    }

    /** 开放多段线首/末端点：弧线段在原顶点辐射方向上的偏移圆弧点 */
    const arcEndpoint = (seg: ArcOff, origVert: PT): PT => {
      const adx = origVert.x - seg.cx,
        ady = origVert.y - seg.cy
      const aLen = Math.hypot(adx, ady)
      if (aLen < 1e-10) {
        return { x: seg.cx + seg.newRadius, y: seg.cy }
      }
      return { x: seg.cx + (adx / aLen) * seg.newRadius, y: seg.cy + (ady / aLen) * seg.newRadius }
    }

    // ── 计算所有新顶点坐标 ───────────────────────────────────────────────

    const n = segOffs.length // 段数
    const newPositions: PT[] = []

    if (polyline.isClosed) {
      // 闭合：n 段 → n 个顶点，顶点 i 是 seg[i-1] 与 seg[i] 的交点
      for (let i = 0; i < n; i++) {
        newPositions.push(intersectSegs(segOffs[(i - 1 + n) % n]!, segOffs[i]!, verts[i]))
      }
    } else {
      // 开放：n 段 → n+1 个顶点
      // 首顶点
      const firstSeg = segOffs[0]!
      newPositions.push(
        firstSeg.type === 'straight' ? firstSeg.p1 : arcEndpoint(firstSeg as ArcOff, verts[0])
      )
      // 中间顶点（各段的交点）
      for (let i = 1; i < n; i++) {
        newPositions.push(intersectSegs(segOffs[i - 1]!, segOffs[i]!, verts[i]))
      }
      // 末顶点
      const lastSeg = segOffs[n - 1]!
      newPositions.push(
        lastSeg.type === 'straight'
          ? lastSeg.p2
          : arcEndpoint(lastSeg as ArcOff, verts[verts.length - 1])
      )
    }

    // ── 重新计算每段的 bulge 值 ──────────────────────────────────────────

    /**
     * 根据偏移后弧线段的两端点和圆心，重新计算 bulge
     * 圆心不变，包含角通过新端点与圆心的方位角差推算
     */
    const recomputeBulge = (p1: PT, p2: PT, seg: SegOff): number => {
      if (seg.type === 'straight') {
        return 0
      }
      const { cx, cy, origBulge } = seg as ArcOff
      const a1 = Math.atan2(p1.y - cy, p1.x - cx)
      const a2 = Math.atan2(p2.y - cy, p2.x - cx)
      let theta: number
      if (origBulge > 0) {
        // CCW：从 a1 逆时针到 a2
        theta = a2 - a1
        if (theta <= 0) {
          theta += Math.PI * 2
        }
      } else {
        // CW：从 a1 顺时针到 a2
        theta = a1 - a2
        if (theta <= 0) {
          theta += Math.PI * 2
        }
      }
      // 保护：theta 接近 2π 时 tan(θ/4) 趋于无穷
      if (theta > Math.PI * 2 - 1e-6) {
        theta = Math.PI * 2 - 1e-6
      }
      return Math.sign(origBulge) * Math.tan(theta / 4)
    }

    // ── 组装新顶点 ───────────────────────────────────────────────────────

    const newVerts: IPolylineVertice[] = []
    const posCount = newPositions.length

    for (let i = 0; i < posCount; i++) {
      const pt = newPositions[i]
      let bulge = 0
      if (i < n) {
        // 段 i 从 newPositions[i] 到下一个顶点
        const nextIdx = polyline.isClosed ? (i + 1) % posCount : i + 1
        if (nextIdx < posCount) {
          bulge = recomputeBulge(pt, newPositions[nextIdx], segOffs[i]!)
        }
      }
      newVerts.push({ x: pt.x, y: pt.y, bulge })
    }

    // ── 有效性校验：偏移距离过大时返回 null ─────────────────────────────

    /** 多边形有符号面积（Shoelace 公式） */
    const signedArea = (pts: { x: number; y: number }[]): number => {
      let a = 0
      for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
        a += pts[j].x * pts[i].y - pts[i].x * pts[j].y
      }
      return a // 不除以 2，只用符号
    }

    if (polyline.isClosed) {
      // 闭合多段线有效性校验
      // 归一化后 d 的含义：段偏移公式使用左转 90° 法线，d>0 = 向左偏移
      //   CCW 多边形（origArea>0）：左 = 内侧 → d>0 = 向内，polygon 应缩小 → newArea < origArea
      //   CW  多边形（origArea<0）：左 = 外侧 → d>0 = 向外，polygon 应扩大（更负） → newArea < origArea
      //   因此无论绕向：d>0 → newArea 应代数上 < origArea；d<0 → newArea 应代数上 > origArea
      const origArea = signedArea(verts)
      const newArea = signedArea(newVerts)
      // 1. 绕向翻转：多边形已坍塌
      if (origArea * newArea <= 0) {
        return null
      }
      // 2. 幅值校验：偏移距离过大时 polygon 不再单调缩小/扩大
      if (d > 0 && newArea >= origArea) {
        return null
      } // 向内但未缩小
      if (d < 0 && newArea <= origArea) {
        return null
      } // 向外但未扩大
    } else {
      // 开放多段线：检查每段新方向与原方向是否一致（点积 > 0），
      // 方向翻转说明两侧偏移段已相互穿越，偏移距离过大
      for (let i = 0; i < n; i++) {
        const origDx = verts[(i + 1) % verts.length].x - verts[i].x
        const origDy = verts[(i + 1) % verts.length].y - verts[i].y
        const newDx = newVerts[i + 1].x - newVerts[i].x
        const newDy = newVerts[i + 1].y - newVerts[i].y
        if (origDx * newDx + origDy * newDy < 0) {
          return null
        }
      }
    }

    const cloned = polyline.clone() as PolylineEntity
    cloned.id = nanoid()
    cloned.vertices = newVerts
    return cloned
  }

  /**
   * 偏移椭圆（近似算法）
   *
   * 椭圆真正的平行偏移曲线并非椭圆，此处采用 CAD 通用近似方案：
   *   长半轴 a' = a + d，短半轴 b' = b + d
   *   新轴比  ratio' = b' / a'
   *   majorAxisEndPoint 等比缩放：方向不变，长度 × (a' / a)
   *
   * d > 0 → 向外扩大；d < 0 → 向内缩小。
   * 当 a + d ≤ 0 或 b + d ≤ 0 时返回 null（椭圆已消失）。
   */
  private static offsetEllipse(ellipse: EllipseEntity, d: number): EllipseEntity | null {
    const a = ellipse.radiusX // 长半轴
    const b = ellipse.radiusY // 短半轴
    const newA = a + d
    const newB = b + d
    if (newA <= 0 || newB <= 0) {
      return null
    }

    const cloned = ellipse.clone() as EllipseEntity
    cloned.id = nanoid()

    // majorAxisEndPoint 是相对于 center 的向量，只需等比缩放
    const scale = newA / a
    cloned.majorAxisEndPoint = {
      x: ellipse.majorAxisEndPoint.x * scale,
      y: ellipse.majorAxisEndPoint.y * scale,
    }
    cloned.axisRatio = newB / newA

    return cloned
  }

  /**
   * 偏移样条曲线
   * 对每个控制点/拟合点沿局部法线方向偏移，重新计算样条
   */
  private static offsetSpline(spline: SplineEntity, d: number): SplineEntity | null {
    /**
     * 对点序列计算各点的法线偏移
     * 法线方向：相邻两段方向向量的平均值后旋转 90°
     */
    const offsetPoints = (pts: IPoint[]): IPoint[] => {
      const n = pts.length
      if (n < 2) {
        return pts.map((p) => ({ x: p.x, y: p.y }))
      }
      return pts.map((p, i) => {
        let tx = 0,
          ty = 0
        if (i > 0) {
          const dx = p.x - pts[i - 1].x
          const dy = p.y - pts[i - 1].y
          const len = Math.hypot(dx, dy)
          if (len > 1e-10) {
            tx += dx / len
            ty += dy / len
          }
        }
        if (i < n - 1) {
          const dx = pts[i + 1].x - p.x
          const dy = pts[i + 1].y - p.y
          const len = Math.hypot(dx, dy)
          if (len > 1e-10) {
            tx += dx / len
            ty += dy / len
          }
        }
        const tLen = Math.hypot(tx, ty)
        if (tLen < 1e-10) {
          return { x: p.x, y: p.y }
        }
        // 法线 = 切线旋转 90°
        const nx = -ty / tLen
        const ny = tx / tLen
        return { x: p.x + nx * d, y: p.y + ny * d }
      })
    }

    const cloned = spline.clone() as SplineEntity
    cloned.id = nanoid()

    if (spline.method === 'FIT' && spline.fitPoints && spline.fitPoints.length >= 2) {
      const newFitPoints = offsetPoints(spline.fitPoints)
      const { controlPoints, knots, degree } = SplineUtils.fitPointsToSpline(
        newFitPoints,
        spline.degree,
        undefined,
        undefined,
        spline.isClosed
      )
      cloned.fitPoints = newFitPoints
      cloned.controlPoints = controlPoints
      cloned.knots = knots
      cloned.degree = degree
    } else if (spline.controlPoints && spline.controlPoints.length >= 2) {
      const newControlPoints = offsetPoints(spline.controlPoints)
      const knots = SplineUtils.generateKnots(
        newControlPoints.length,
        spline.degree,
        spline.isClosed
      )
      cloned.controlPoints = newControlPoints
      cloned.knots = knots
    } else {
      return null
    }

    return cloned
  }

  /************ 偏移方向计算 ************/
  /**
   * 根据鼠标位置计算偏移方向。
   *
   * +1：外侧（圆弧外、多段线左侧）；-1：内侧（圆弧内、多段线右侧）。
   *
   * @param entity - 源实体
   * @param mousePos - 鼠标位置世界坐标
   * @returns 偏移方向
   */
  static computeSide(entity: IEntity, mousePos: { x: number; y: number }): OffsetSide {
    const { x: mx, y: my } = mousePos
    switch (entity.type) {
      case EntityType.Circle:
      case EntityType.Arc: {
        const e = entity as CircleEntity | ArcEntity
        return Math.hypot(mx - e.center.x, my - e.center.y) >= e.radius ? 1 : -1
      }
      case EntityType.Polyline:
        return OffsetUtils.polylineSide(entity as PolylineEntity, mx, my)
      case EntityType.Spline:
        return OffsetUtils.splineSide(entity as SplineEntity, mx, my)
      case EntityType.Ellipse: {
        const e = entity as EllipseEntity
        // 将椭圆坐标转换到单位圆坐标系，判断点在内外
        const angle = Math.atan2(e.majorAxisEndPoint.y, e.majorAxisEndPoint.x)
        const cosA = Math.cos(-angle)
        const sinA = Math.sin(-angle)
        const dx = mx - e.center.x
        const dy = my - e.center.y
        const xRot = dx * cosA - dy * sinA
        const yRot = dx * sinA + dy * cosA
        const norm =
          (xRot * xRot) / (e.radiusX * e.radiusX) + (yRot * yRot) / (e.radiusY * e.radiusY)
        return norm >= 1 ? 1 : -1
      }
      default:
        return 1
    }
  }

  /**
   * 计算鼠标相对于多段线的方向
   * - 闭合多段线：射线法判断鼠标是否在多边形内侧（内侧返回 -1，外侧返回 1）
   * - 开放多段线：取各段叉积累计值的符号，判断鼠标在左（+1）还是右（-1）
   *
   * 注：对于闭合多段线，叉积累计值等于 2×有符号面积，与鼠标坐标无关，
   * 因此不能用于判断内外侧，必须使用点包含测试。
   */
  private static polylineSide(polyline: PolylineEntity, mx: number, my: number): 1 | -1 {
    const verts = polyline.vertices
    if (verts.length < 2) {
      return 1
    }
    if (polyline.isClosed && verts.length >= 3) {
      // 闭合：射线法判断鼠标是否在多边形内部
      return OffsetUtils.isPointInPolygon(verts, mx, my) ? -1 : 1
    }
    // 开放：找离鼠标最近的线段，用该段叉积判断左右侧
    return OffsetUtils.sideByNearestSegment(verts, mx, my)
  }

  /**
   * 计算鼠标相对于样条曲线的方向
   * - 闭合样条：射线法判断是否在内部（内侧返回 -1，外侧返回 1）
   * - 开放样条：叉积累计值判断左右侧
   */
  private static splineSide(spline: SplineEntity, mx: number, my: number): 1 | -1 {
    const pts =
      spline.method === 'FIT' && spline.fitPoints?.length ? spline.fitPoints : spline.controlPoints
    if (!pts || pts.length < 2) {
      return 1
    }
    if (spline.isClosed && pts.length >= 3) {
      return OffsetUtils.isPointInPolygon(pts, mx, my) ? -1 : 1
    }
    // 开放：找离鼠标最近的线段，用该段叉积判断左右侧
    return OffsetUtils.sideByNearestSegment(pts, mx, my)
  }

  /**
   * 找到距离鼠标最近的线段，并用该段的叉积判断鼠标在左侧（+1）还是右侧（-1）
   * 适用于开放折线/样条等开放曲线的偏移方向判断
   */
  private static sideByNearestSegment(
    pts: { x: number; y: number }[],
    mx: number,
    my: number
  ): 1 | -1 {
    let minDist = Infinity
    let cross = 0
    for (let i = 0; i < pts.length - 1; i++) {
      const ax = pts[i].x,
        ay = pts[i].y
      const bx = pts[i + 1].x,
        by = pts[i + 1].y
      const dx = bx - ax,
        dy = by - ay
      const lenSq = dx * dx + dy * dy
      // 鼠标在线段上的投影参数 t（夹到 [0,1]）
      const t = lenSq > 0 ? Math.max(0, Math.min(1, ((mx - ax) * dx + (my - ay) * dy) / lenSq)) : 0
      const closestX = ax + t * dx
      const closestY = ay + t * dy
      const dist = Math.hypot(mx - closestX, my - closestY)
      if (dist < minDist) {
        minDist = dist
        // 叉积 (b-a) × (mouse-a)：正值表示鼠标在行进方向左侧
        cross = dx * (my - ay) - dy * (mx - ax)
      }
    }
    return cross >= 0 ? 1 : -1
  }

  /**
   * 射线法（Ray Casting）判断点 (mx, my) 是否在多边形内部
   * 适用于任意简单多边形（无需判断顺/逆时针）
   */
  private static isPointInPolygon(
    polygon: { x: number; y: number }[],
    mx: number,
    my: number
  ): boolean {
    let inside = false
    const n = polygon.length
    for (let i = 0, j = n - 1; i < n; j = i++) {
      const xi = polygon[i].x,
        yi = polygon[i].y
      const xj = polygon[j].x,
        yj = polygon[j].y
      const intersects = yi > my !== yj > my && mx < ((xj - xi) * (my - yi)) / (yj - yi) + xi
      if (intersects) {
        inside = !inside
      }
    }
    return inside
  }
}
